/**
 * Os dados do agendamento: o passo 3 da "Nova consulta", com os campos do
 * "Horário Vago" do SISAC (fase 2 da agenda da clínica).
 *
 * ── O paciente ───────────────────────────────────────────────────────────
 *
 * Vinculado ao contato, aparece como "[código] - nome", e o cadastro dele
 * pré-preenche o resto. Sem vínculo, o bloco "Paciente no SISAC" (que vem por
 * prop, para o widget não depender de como se procura) busca pelo CPF, nome ou
 * código e vincula. Quem o SISAC não tem pode ir sem cadastro, só pelo nome.
 *
 * ── As listas ────────────────────────────────────────────────────────────
 *
 * Convênio, plano, origem e local vêm das operações da clínica. Sem a operação
 * ligada, o campo vira texto (o código, como o SISAC grava).
 */

import type { ReactNode } from 'react';
import { Botao, Campo, CampoArea, CampoSelecao, Interruptor } from '@/shared/ui';
import { Carregando } from '@/shared/ui/estados/estados';
import {
  SEXOS,
  TIPOS_DE_AGENDAMENTO,
  idadeEmPalavras,
  mascararCep,
  type CampoDoFormulario,
  type FormularioDoAgendamento,
  type OpcaoDaLista,
} from '../lib/formulario-do-agendamento';
import { cpfProtegido } from '@/entities/saude/paciente';
import { ESPERA_DO_SISAC } from '../lib/consultas-na-tela';
import type { ListaDoFormulario, PacienteDaConsulta } from '../lib/consultas-na-tela';
import './dados-do-agendamento.css';

type Erros = Partial<Record<CampoDoFormulario | 'paciente', string>>;

export function DadosDoAgendamento({
  formulario,
  erros,
  listas,
  paciente,
  buscaDoPaciente,
  especialidade,
  hoje,
  travado,
  aoMudar,
}: {
  formulario: FormularioDoAgendamento;
  /** Os erros a mostrar: vazio até a primeira tentativa de agendar. */
  erros: Erros;
  listas: {
    readonly convenios: ListaDoFormulario;
    readonly planos: ListaDoFormulario;
    readonly origens: ListaDoFormulario;
    readonly locais: ListaDoFormulario;
  };
  paciente: PacienteDaConsulta;
  /** O bloco que procura e vincula o paciente no SISAC; nulo sem cliente na oportunidade. */
  buscaDoPaciente: ReactNode;
  especialidade: string;
  hoje: Date;
  travado: boolean;
  aoMudar: (mudanca: Partial<FormularioDoAgendamento>) => void;
}): ReactNode {
  const idade = idadeEmPalavras(formulario.nascimento, hoje);
  const vinculado = paciente.situacao === 'vinculado' ? paciente.paciente : null;

  return (
    <div className="dados-do-agendamento">
      <div className="dados-do-agendamento__paciente" role="group" aria-labelledby="dados-paciente">
        <div className="dados-do-agendamento__cab">
          <h5 className="dados-do-agendamento__titulo" id="dados-paciente">
            Paciente
          </h5>
          {!vinculado && (
            <Botao
              variante="fantasma"
              miudo
              desabilitado={travado}
              motivo={ESPERA_DO_SISAC}
              aria-pressed={formulario.semCadastro}
              onClick={() => aoMudar({ semCadastro: !formulario.semCadastro })}
            >
              {formulario.semCadastro ? 'Procurar no SISAC' : 'Agendar sem cadastro'}
            </Botao>
          )}
        </div>
        {paciente.situacao === 'lendo' ? (
          <Carregando forma="linha" linhas={1} rotulo="Lendo o paciente no SISAC…" />
        ) : vinculado ? (
          <p className="dados-do-agendamento__vinculado">
            <strong>
              {vinculado.codigo} - {vinculado.nome}
            </strong>
            {vinculado.finalDoCpf ? (
              <span className="dados-do-agendamento__meta">
                CPF {cpfProtegido(vinculado.finalDoCpf)}
              </span>
            ) : null}
          </p>
        ) : formulario.semCadastro ? (
          <Campo
            id="dados-nome"
            rotulo="Nome completo do paciente"
            autoComplete="off"
            value={formulario.nome}
            erro={erros.nome}
            ajuda="O paciente vai sem cadastro no SISAC, só com estes dados."
            onChange={(e) => aoMudar({ nome: e.currentTarget.value })}
          />
        ) : (
          <>
            {buscaDoPaciente ?? (
              <p className="dados-do-agendamento__nota">
                A busca do paciente no SISAC não está disponível aqui. Agende sem cadastro.
              </p>
            )}
            {erros.paciente && (
              <p className="dados-do-agendamento__erro" role="alert">
                {erros.paciente}
              </p>
            )}
          </>
        )}
      </div>

      <div className="dados-do-agendamento__campos">
        <Campo
          id="dados-especialidade"
          rotulo="Especialidade da consulta"
          value={especialidade}
          readOnly
        />
        <Lista
          id="dados-local"
          rotulo="Local"
          lista={listas.locais}
          valor={formulario.local}
          erro={erros.local}
          travado={travado}
          aoEscolher={(local) => aoMudar({ local })}
        />
        <Lista
          id="dados-origem"
          rotulo="Origem"
          ajuda="Por onde o pedido chegou."
          lista={listas.origens}
          valor={formulario.origem}
          erro={erros.origem}
          travado={travado}
          aoEscolher={(origem) => aoMudar({ origem })}
        />
        <CampoSelecao
          id="dados-tipo"
          rotulo="Tipo"
          value={formulario.tipo}
          erro={erros.tipo}
          disabled={travado}
          onChange={(e) => aoMudar({ tipo: e.currentTarget.value })}
        >
          {TIPOS_DE_AGENDAMENTO.map((tipo) => (
            <option key={tipo.valor} value={tipo.valor}>
              {tipo.rotulo}
            </option>
          ))}
        </CampoSelecao>
        <Campo
          id="dados-nascimento"
          rotulo="Nascimento"
          type="date"
          value={formulario.nascimento}
          erro={erros.nascimento}
          max={hoje.toISOString().slice(0, 10)}
          disabled={travado}
          onChange={(e) => aoMudar({ nascimento: e.currentTarget.value })}
        />
        <Campo
          id="dados-idade"
          rotulo="Idade"
          value={idade ?? ''}
          placeholder="Calculada pelo nascimento"
          readOnly
        />
        <CampoSelecao
          id="dados-sexo"
          rotulo="Sexo"
          value={formulario.sexo}
          disabled={travado}
          onChange={(e) => aoMudar({ sexo: e.currentTarget.value })}
        >
          <option value="">Não informado</option>
          {SEXOS.map((sexo) => (
            <option key={sexo.valor} value={sexo.valor}>
              {sexo.rotulo}
            </option>
          ))}
        </CampoSelecao>
        <Campo
          id="dados-cep"
          rotulo="CEP"
          inputMode="numeric"
          autoComplete="off"
          placeholder="00000-000"
          value={formulario.cep}
          erro={erros.cep}
          disabled={travado}
          onChange={(e) => aoMudar({ cep: mascararCep(e.currentTarget.value) })}
        />
        <Lista
          id="dados-convenio"
          rotulo="Convênio"
          lista={listas.convenios}
          valor={formulario.convenio}
          erro={erros.convenio}
          travado={travado}
          aoEscolher={(convenio) => aoMudar({ convenio, plano: '' })}
        />
        <Lista
          id="dados-plano"
          rotulo="Plano"
          lista={listas.planos}
          valor={formulario.plano}
          erro={erros.plano}
          travado={travado || formulario.convenio === ''}
          motivoVazio="Escolha o convênio primeiro."
          aoEscolher={(plano) => aoMudar({ plano })}
        />
        <Campo
          id="dados-matricula"
          rotulo="Matrícula"
          ajuda="A carteirinha do convênio; no particular, SN."
          autoComplete="off"
          value={formulario.matricula}
          disabled={travado}
          onChange={(e) => aoMudar({ matricula: e.currentTarget.value })}
        />
        {!vinculado && (
          <Campo
            id="dados-cpf"
            rotulo="CPF (opcional)"
            inputMode="numeric"
            autoComplete="off"
            value={formulario.cpf}
            erro={erros.cpf}
            disabled={travado}
            onChange={(e) => aoMudar({ cpf: e.currentTarget.value })}
          />
        )}
        <Campo
          id="dados-whatsapp"
          rotulo="WhatsApp"
          inputMode="tel"
          autoComplete="off"
          value={formulario.whatsapp}
          erro={erros.whatsapp}
          ajuda="Vem do telefone do contato."
          disabled={travado}
          onChange={(e) => aoMudar({ whatsapp: e.currentTarget.value })}
        />
        <Campo
          id="dados-celular"
          rotulo="Celular"
          inputMode="tel"
          autoComplete="off"
          value={formulario.celular}
          erro={erros.celular}
          disabled={travado}
          onChange={(e) => aoMudar({ celular: e.currentTarget.value })}
        />
        <Campo
          id="dados-email"
          rotulo="E-mail"
          type="email"
          autoComplete="off"
          value={formulario.email}
          erro={erros.email}
          disabled={travado}
          onChange={(e) => aoMudar({ email: e.currentTarget.value })}
        />
      </div>

      <div className="dados-do-agendamento__opcoes">
        <Interruptor
          rotulo="Telemedicina"
          ligado={formulario.telemedicina}
          desabilitado={travado}
          motivo={ESPERA_DO_SISAC}
          aoAlternar={() => aoMudar({ telemedicina: !formulario.telemedicina })}
        />
        <Interruptor
          rotulo="Urgente"
          ligado={formulario.urgente}
          desabilitado={travado}
          motivo={ESPERA_DO_SISAC}
          aoAlternar={() => aoMudar({ urgente: !formulario.urgente })}
        />
      </div>
      <CampoArea
        id="dados-observacao"
        rotulo="Observação"
        rows={2}
        value={formulario.observacao}
        disabled={travado}
        onChange={(e) => aoMudar({ observacao: e.currentTarget.value })}
      />
    </div>
  );
}

/** Um campo de lista do SISAC: escolha quando a operação está ligada, texto quando não. */
function Lista({
  id,
  rotulo,
  ajuda,
  lista,
  valor,
  erro,
  travado,
  motivoVazio,
  aoEscolher,
}: {
  id: string;
  rotulo: string;
  ajuda?: string;
  lista: ListaDoFormulario;
  valor: string;
  erro?: string;
  travado: boolean;
  motivoVazio?: string;
  aoEscolher: (valor: string) => void;
}): ReactNode {
  if (lista.situacao === 'desligada') {
    return (
      <Campo
        id={id}
        rotulo={rotulo}
        ajuda={ajuda ?? 'O código, como o SISAC grava.'}
        autoComplete="off"
        value={valor}
        erro={erro}
        disabled={travado}
        onChange={(e) => aoEscolher(e.currentTarget.value)}
      />
    );
  }
  const opcoes: readonly OpcaoDaLista[] = lista.situacao === 'pronta' ? lista.opcoes : [];
  const vazia = lista.situacao === 'pronta' && opcoes.length === 0;
  return (
    <CampoSelecao
      id={id}
      rotulo={rotulo}
      ajuda={lista.situacao === 'erro' ? undefined : ajuda}
      value={valor}
      erro={erro ?? (lista.situacao === 'erro' ? lista.recado : undefined)}
      disabled={travado || lista.situacao !== 'pronta'}
      onChange={(e) => aoEscolher(e.currentTarget.value)}
    >
      <option value="">
        {lista.situacao === 'carregando'
          ? 'Carregando…'
          : vazia
            ? (motivoVazio ?? 'Nada para escolher')
            : 'Escolha'}
      </option>
      {opcoes.map((opcao) => (
        <option key={opcao.valor} value={opcao.valor}>
          {opcao.rotulo}
        </option>
      ))}
    </CampoSelecao>
  );
}
